import { useState } from 'react';
import { fetchMyDay, fetchWaitingList, type MyDay, type WaitingList } from '../api';
import { usePolling } from '../hooks/usePolling';

const SOURCE_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp', meta_form: 'Meta form', meta_ad: 'Meta', facebook: 'Facebook', social: 'Instagram',
  instagram: 'Instagram', website: 'Website', call: 'Call',
};

function ago(iso: string | null) {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} d`;
}

// The few leads to work first. Read-only: opening a lead uses the normal lead screen.
export default function WaitingForYou({ active, reloadKey, onSelect }: { active: boolean; reloadKey: number; onSelect: (id: string) => void }) {
  const [data, setData] = useState<WaitingList | null>(null);
  const [open, setOpen] = useState(true);
  const [day, setDay] = useState<MyDay | null>(null);

  usePolling(async () => {
    try { setData(await fetchWaitingList()); } catch { /* keep the last list; the queue below still works */ }
    try { setDay(await fetchMyDay()); } catch { /* the day line is optional */ }
  }, 60_000, [reloadKey], { enabled: active, immediate: true });

  if (!data) return null;
  const buckets = data.buckets.filter((b) => b.items.length > 0);

  return (
    <>
    {day && (
      <p className="mb-2 px-1 text-xs text-slate-600 tabular-nums">
        <span className="font-semibold text-slate-800">Today:</span> {day.calls} calls · {day.reached} reached · {day.results} results recorded
        {day.positives > 0 && <> · <span className="font-semibold text-emerald-700">{day.positives} Positive</span></>} · {day.follow_ups_due} follow-ups due
      </p>
    )}
    <section className="mb-3 rounded-xl border border-emerald-200 bg-white">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between px-3 py-2 text-left">
        <span className="text-sm font-semibold text-slate-900">Waiting for you</span>
        <span className="flex gap-1.5 text-[11px] font-semibold tabular-nums">
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800">Yours {data.waiting_mine}</span>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">Unclaimed {data.waiting_unclaimed}</span>
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-3 pb-2">
          {buckets.length === 0 && <p className="py-3 text-center text-xs text-slate-500">Nothing urgent. Continue with your queue below.</p>}
          {buckets.map((bucket) => (
            <div key={bucket.key} className="pt-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {bucket.label} · {bucket.total}{bucket.hidden > 0 ? ` (${bucket.hidden} more)` : ''}
              </p>
              <ul className="mt-1 space-y-1">
                {bucket.items.map((item) => (
                  <li key={item.id}>
                    <button type="button" onClick={() => onSelect(item.id)}
                      className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-left hover:border-emerald-300 hover:bg-emerald-50">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="truncate font-medium text-slate-900">{item.name || 'Unknown'}</span>
                        <span className="flex-none rounded bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-600">{SOURCE_LABEL[item.source] ?? item.source}</span>
                        {item.unassigned && <span className="flex-none rounded bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800">Unassigned</span>}
                        <span className="ml-auto flex-none text-[11px] tabular-nums text-slate-500">
                          {item.waiting_since ? `waiting ${ago(item.waiting_since)}` : ago(item.created_at)}
                        </span>
                      </div>
                      <p className="mt-0.5 line-clamp-1 text-xs text-slate-600">{item.opening_line}</p>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="pt-2 text-[11px] text-slate-400">Older leads in your queue: {data.older_in_my_queue}</p>
        </div>
      )}
    </section>
    </>
  );
}
